import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const SITE_URL = 'https://gerald-fasching.at';
export const SITE_NAME = 'Gerald Fasching';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/about/about-gerald.jpg`;

export interface SeoProps {
  title: string;
  description: string;
  /** Path-only canonical (e.g. "/ueber-mich"). Falls back to current location. */
  path?: string;
  /** Absolute or site-root-relative image URL for og:image. Falls back to DEFAULT_OG_IMAGE. */
  image?: string;
  /** "article" for blog posts, otherwise "website" */
  type?: 'website' | 'article';
  /** When true, instructs crawlers to skip the page (e.g. preview/internal/404) */
  noindex?: boolean;
  /** Optional JSON-LD payload(s) to inject for the page */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** ISO date for article published_time (blog detail) */
  articlePublishedTime?: string;
  /** ISO date for article modified_time */
  articleModifiedTime?: string;
  /** Article author name */
  articleAuthor?: string;
  /** Article section/category */
  articleSection?: string;
}

const META_MARKER = 'data-seo-managed';
const JSONLD_MARKER = 'data-seo-jsonld';

function setMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(META_MARKER, '');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"][${META_MARKER}]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute(META_MARKER, '');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function clearJsonLd() {
  document.head.querySelectorAll(`script[${JSONLD_MARKER}]`).forEach((n) => n.remove());
}

function appendJsonLd(payload: Record<string, unknown>) {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute(JSONLD_MARKER, '');
  script.text = JSON.stringify(payload);
  document.head.appendChild(script);
}

function absoluteUrl(value?: string): string {
  if (!value) return DEFAULT_OG_IMAGE;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `${SITE_URL}${value.startsWith('/') ? value : `/${value}`}`;
}

export default function Seo({
  title,
  description,
  path,
  image,
  type = 'website',
  noindex = false,
  jsonLd,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleSection,
}: SeoProps) {
  const location = useLocation();
  const resolvedPath = path ?? location.pathname;
  const canonical = `${SITE_URL}${resolvedPath === '/' ? '/' : resolvedPath.replace(/\/$/, '')}`;
  const ogImage = absoluteUrl(image);

  useEffect(() => {
    document.title = title;

    setMeta('meta[name="description"]', { name: 'description', content: description });
    setMeta('meta[name="robots"]', {
      name: 'robots',
      content: noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1',
    });

    setLink('canonical', canonical);

    setMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    setMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    setMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    setMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'de_AT' });
    setMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });
    setMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: title });

    setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage });

    const articleTags: Array<[string, string | undefined]> = [
      ['article:published_time', articlePublishedTime],
      ['article:modified_time', articleModifiedTime],
      ['article:author', articleAuthor],
      ['article:section', articleSection],
    ];
    articleTags.forEach(([prop, val]) => {
      const existing = document.head.querySelector<HTMLMetaElement>(`meta[property="${prop}"]`);
      if (type === 'article' && val) {
        setMeta(`meta[property="${prop}"]`, { property: prop, content: val });
      } else if (existing) {
        existing.remove();
      }
    });

    clearJsonLd();
    if (jsonLd) {
      const list = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      list.forEach(appendJsonLd);
    }
  }, [
    title,
    description,
    canonical,
    ogImage,
    type,
    noindex,
    JSON.stringify(jsonLd ?? null),
    articlePublishedTime,
    articleModifiedTime,
    articleAuthor,
    articleSection,
  ]);

  return null;
}

export function buildBreadcrumbJsonLd(
  trail: Array<{ name: string; path: string }>
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((step, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: step.name,
      item: `${SITE_URL}${step.path}`,
    })),
  };
}
